import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Save, Image as ImageIcon, Upload, X } from "lucide-react";
import { toast } from "sonner";

export default function Settings() {
  const [form, setForm] = useState({ name: "", cnpj: "", address: "", phone: "", email: "", logo_url: "" });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 1024 * 1024) return toast.error("A imagem deve ter no máximo 1 MB");
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post("/company/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, logo_url: data.logo_url }));
      toast.success("Logo enviada");
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao enviar logo"); }
    finally { setUploading(false); }
  };

  useEffect(() => {
    api.get("/company").then((r) => setForm((f) => ({ ...f, ...r.data }))).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/company", form);
      toast.success("Configurações salvas");
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="settings-page">
      <div>
        <div className="text-xs uppercase tracking-widest text-slate-500 mb-1">Administração</div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight">Configurações da Empresa</h1>
      </div>

      <Card className="card-outline">
        <CardContent className="p-6 space-y-4">
          <div className="flex items-start gap-4">
            {form.logo_url ? (
              <img src={form.logo_url} alt="Logo" className="w-24 h-24 object-contain border rounded-lg bg-white" />
            ) : (
              <div className="w-24 h-24 flex items-center justify-center border-2 border-dashed border-slate-200 rounded-lg text-slate-400">
                <ImageIcon className="w-8 h-8" />
              </div>
            )}
            <div className="flex-1">
              <Label>Logomarca</Label>
              <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml"
                className="hidden" onChange={uploadLogo} data-testid="logo-file-input" />
              <div className="flex gap-2 mt-1">
                <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="logo-upload-btn">
                  <Upload className="w-4 h-4 mr-2" /> {uploading ? "Enviando..." : "Enviar logo do computador"}
                </Button>
                {form.logo_url && (
                  <Button type="button" variant="ghost" className="text-rose-600" onClick={() => setForm({ ...form, logo_url: "" })} data-testid="logo-remove-btn">
                    <X className="w-4 h-4 mr-1" /> Remover
                  </Button>
                )}
              </div>
              <p className="text-xs text-slate-500 mt-2">PNG, JPG, WEBP ou SVG até 1 MB. Aparece nos recibos de venda e orçamentos impressos. Para remover, clique em Remover e depois em Salvar.</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div><Label>Nome da empresa</Label>
              <Input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="co-name" /></div>
            <div><Label>CNPJ</Label>
              <Input value={form.cnpj || ""} onChange={(e) => setForm({ ...form, cnpj: e.target.value })} data-testid="co-cnpj" placeholder="29.421.612/0001-30" /></div>
            <div><Label>Telefone</Label>
              <Input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="co-phone" /></div>
            <div><Label>E-mail</Label>
              <Input type="email" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="co-email" /></div>
          </div>
          <div><Label>Endereço</Label>
            <Textarea value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="co-address" /></div>

          <div className="flex justify-end">
            <Button onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="save-settings-btn">
              <Save className="w-4 h-4 mr-2" /> Salvar
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
